import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Box, Grid, LinearProgress, Typography } from '@material-ui/core';
import type { ProjectFormData } from '../../interfaces/ui/addProjectModal';
import { ProjectsHeader } from './components/ProjectsHeader';
import { IqeProjectCard } from '../ProjectCard';

import { EmptyProjectsState } from './components/EmptyProjectsState';
import { RegenerateConfirmDialog } from './components/RegenerateConfirmDialog';
import { AddProjectModal } from '../AddProjectModal';
import { ExecutionCenter } from '../ExecutionCenter';
import BackButton from '../../../../../utils/backButton';
import { HelpComponent } from '../../../../../utils/helpComponent';
import HELP_URL from '../../../../../utils/helpLinkConstant';

import { useProjectsState } from '../../hooks/useProjectsState';
import { useModalState } from '../../hooks/useModalState';
import useTestcaseExecutionState from '../../hooks/useTestcaseExecutionState';
import useRegenerateState from '../../hooks/useRegenerateState';

// import useProjectDetailsState from '../../hooks/useProjectDetailsState';
import '../../styles/projectsList.css';

export const IqeProjectsList = ({ isToolAdmin }: { isToolAdmin: boolean }) => {
  const { projectId } = useParams();
  const {
    projects,
    loadingIqeProjects,
    saveProject,
    isSavingProject,
    getProjectById,
    // updateProjectsAfterExecution,
    refreshProjectStatus,
    refreshingProjectIds,
    rerunProjectValidation,
    rerunningProjectIds,
    performProjectOperation,
  } = useProjectsState(projectId!);

  // const { projectDetailsState, openProjectDetails, closeProjectDetails } =
  //   useProjectDetailsState();
  const { executionState, startExecution, closeExecution, openResults } =
    useTestcaseExecutionState();

  const {
    regenerateState,
    openRegenerateDialog,
    closeRegenerateDialog,
    confirmRegenerate,
  } = useRegenerateState();
  const { modalState, openModal, closeModal } = useModalState();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  // Start execution triggered by returning from ProjectDetailsPage
  useEffect(() => {
    const routeState = (location.state as any) || {};
    const trigger = routeState.triggerExecution;
    const openResultsProjectId = routeState.openResultsProjectId;

    if (trigger) {
      const startTriggeredExecution = async () => {
        const selectedProject = getProjectById(trigger.projectId);
        const hasPreviousRuns = (selectedProject?.runs?.length || 0) > 0;
        const operation =
          trigger.mode === 'RUN'
            ? 'EXECUTE'
            : selectedProject?.status === 'GENERATE_FAILED' && !hasPreviousRuns
            ? 'GENERATE_RETRY'
            : 'GENERATE';
        const runId = await performProjectOperation(
          projectId!,
          trigger.projectId,
          operation,
        );
        if (runId) {
          startExecution(
            trigger.projectId,
            trigger.projectName,
            trigger.mode,
            runId,
          );
        }
        navigate('.', { replace: true, state: {} });
      };

      startTriggeredExecution();
      return;
    }

    if (openResultsProjectId) {
      openResults(openResultsProjectId);
      navigate('.', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleAddProject = useCallback(
    async (projectData: ProjectFormData) => {
      await saveProject(projectData, projectId!);
      closeModal();
    },
    [closeModal, modalState.editingProjectId, projectId],
  );

  const handleRefreshStatus = useCallback(
    (iqeProjectId: string) => {
      refreshProjectStatus(projectId!, iqeProjectId);
    },
    [projects],
  );

  const handleRerunValidation = useCallback(
    (iqeProjectId: string) => {
      rerunProjectValidation(iqeProjectId);
    },
    [rerunProjectValidation],
  );
  const handleGenerateTests = useCallback(
    async (iqeProjectId: string) => {
      const project = getProjectById(iqeProjectId);
      if (!project) return;
      const hasPreviousRuns = (project.runs?.length || 0) > 0;
      const operation =
        project.status === 'GENERATE_FAILED' && !hasPreviousRuns
          ? 'GENERATE_RETRY'
          : 'GENERATE';

      const runId = await performProjectOperation(
        projectId!,
        iqeProjectId,
        operation,
      );
      if (runId) {
        startExecution(iqeProjectId, project.name, operation, runId);
      }
    },
    [getProjectById, performProjectOperation, projectId, startExecution],
  );

  const handleRunTests = useCallback(
    (iqeProjectId: string) => {
      const project = getProjectById(iqeProjectId);
      if (project && project.transactionId)
        startExecution(
          iqeProjectId,
          project.name,
          'RUN',
          project.transactionId,
        );
    },
    [projects],
  );

  const handleRegenerateTests = useCallback(
    (iqeProjectId: string) => {
      openRegenerateDialog(iqeProjectId);
    },
    [openRegenerateDialog],
  );

  const handleExecutionClose = useCallback(() => {
    // updateProjectsAfterExecution(
    //   executionState.executingProjectId,
    //   executionState.executingMode,
    //   projects,
    // );
    // setProjects(updated); TODO: Update projects state after execution
    closeExecution();
  }, [executionState, projects, closeExecution]);

  const handleViewResults = useCallback(
    (selectedProjectId: string) => {
      openResults(selectedProjectId);
    },
    [openResults],
  );

  const handleViewProjectDetails = useCallback(
    (iqeProjectId: string) => {
      navigate(iqeProjectId);
    },
    [navigate],
  );

  const handleViewCurrentStatus = useCallback(
    (iqeProjectId: string, projectName: string, transactionId: string) => {
      if (transactionId)
        startExecution(iqeProjectId, projectName, 'RUN', transactionId);
    },
    [startExecution],
  );

  const handleOpenAddModal = useCallback(() => {
    openModal();
  }, [openModal]);

  const handleCloseModal = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleConfirmRegenerate = useCallback(() => {
    confirmRegenerate(iqeProjectId => {
      const project = getProjectById(iqeProjectId);
      if (project && project.transactionId)
        startExecution(
          iqeProjectId,
          project.name,
          'REGENERATE',
          project.transactionId,
        );
    });
  }, [getProjectById, projects]);

  const handleCancelRegenerate = useCallback(() => {
    closeRegenerateDialog();
  }, [closeRegenerateDialog]);

  const navBar = (
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <BackButton />
      <HelpComponent helpUrl={HELP_URL.PERFORMANCE_TEST_RUNS_PAGE} />
    </Grid>
  );

  const filteredProjects = searchQuery.trim()
    ? projects.filter(
        p =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.appUrl.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.environment.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : projects;

  if (loadingIqeProjects) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="body1"
            align="center"
            style={{ marginTop: '16px' }}
          >
            Fetching IQE Projects data...
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      {navBar}

      {executionState.executingProjectId ? (
        <ExecutionCenter
          isOpen
          mode={executionState.executingMode}
          projectName={executionState.executingProjectName}
          projectId={executionState.executingProjectId}
          apmNumber={projectId!}
          runId={executionState.executingRunId || undefined}
          environment={
            projects.find(p => p.id === executionState.executingProjectId)
              ?.environment || ''
          }
          testPlanName={
            projects.find(p => p.id === executionState.executingProjectId)
              ?.testPlanName || ''
          }
          onClose={handleExecutionClose}
          onViewDetailedResults={handleViewResults}
        />
      ) : (
        <main>
          <ProjectsHeader
            isToolAdmin={isToolAdmin}
            projectCount={filteredProjects.length}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onAddProject={handleOpenAddModal}
          />

          {!loadingIqeProjects && projects.length === 0 && (
            <EmptyProjectsState
              onAddProject={handleOpenAddModal}
              isToolAdmin={isToolAdmin}
            />
          )}
          {!loadingIqeProjects && projects.length > 0 && (
            <Box
              component="section"
              className="projects-grid-section"
              aria-labelledby="projects-grid-title"
            >
              <Box className="projects-grid">
                {filteredProjects.map(project => (
                  <IqeProjectCard
                    key={project.id}
                    isToolAdmin={isToolAdmin}
                    project={project}
                    isRefreshingStatus={refreshingProjectIds.includes(
                      project.id,
                    )}
                    onRefreshStatus={handleRefreshStatus}
                    onGenerateTests={handleGenerateTests}
                    onViewCurrentStatus={handleViewCurrentStatus}
                    onRunTests={handleRunTests}
                    onRegenerateTests={handleRegenerateTests}
                    onViewResults={handleViewResults}
                    isRerunning={rerunningProjectIds.includes(project.id)}
                    onRerunValidation={handleRerunValidation}
                    onViewProjectDetails={handleViewProjectDetails}
                  />
                ))}
              </Box>
            </Box>
          )}

          <AddProjectModal
            isOpen={modalState.isOpen}
            onClose={handleCloseModal}
            onSubmit={handleAddProject}
            isSaving={isSavingProject}
            initialData={modalState.editingProjectData || undefined}
            initialErrors={modalState.initialErrors}
            isEditing={modalState.isEditing}
          />

          <RegenerateConfirmDialog
            isOpen={regenerateState.showConfirmation}
            project={getProjectById(regenerateState.projectId || '')}
            onConfirm={handleConfirmRegenerate}
            onCancel={handleCancelRegenerate}
          />
        </main>
      )}
    </>
  );
};
